import './index.css';
import { SearchOutlined, LeftOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Form, Select, message, Modal, Input, Radio, Alert, Carousel, Image, Row, Col, Typography} from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import { api, useAPI } from '../../api';
import DrugClassResultsTableNew from '../../components/DrugClassResultsTableNew';
import {printDrugClassReport} from '../../components/printDrugClassReport';
import debounce from 'lodash.debounce';
import { initDetailCache, ensureDetailsForDrug, drugKeyFromRecord } from '../../components/detailCapture'; // path adjust
// import FormSuggestInput from '../../components/FormSuggestInput';
// import ContactModal from '../../components/ContactModal';
import StatusSelect from './StatusInput';
const { Text } = Typography;
const SEARCH_METHOD = {
  BY_PARTD: 'byPartD',
  BY_COUNTY_INSURANCE: 'byCountyInsurance',
};


const SEARCH_FORM_FIELDS = {
    PartDCode: 'partDCode',
    DrugClass: 'drugClass',
    SelectedPlan: 'selectedPlan',
    CountyCode: 'countyCode',
    Plan: 'plan',
    ZipCode: 'zipCode',
};

const drugClassSelectOptions = [
  {
    label: 'GLP-1 Agonists / GIP GLP-1 Agonists',
    value: 'GLP-1 Receptor Agonist',
  },
  {
    label: 'SGLT2 Inhibitors',
    value: 'Sodium-Glucose Cotransporter 2 Inhibitor',
  },
  {
    label: 'All Insulin',
    value: 'insulin',
  },
  {
    label: '  - Rapid/Short-Acting Insulin (U-100)',
    value: 'Rapid-Short-Acting Insulin',
  },
  {
    label: '  - Intermediate/Long-Acting Insulin (U-100)',
    value: 'Intermediate-Long-Acting Insulin',
  },
  {
    label: '  - Mixed Insulin',
    value: 'Mixed Insulin',
  },
  {
    label: '  - Concentrated Insulin',
    value: 'Concentrated Insulin',
  },

  {
    label: '  - Insulin/GLP-1 RA Combinations',
    value: 'Insulin_GLP-1 RA Combinations',
  },
  {
    label: 'Finerenone',
    value: 'Finerenone',
  },
  {
    label: 'DPP-4 Inhibitors',
    value: 'Dipeptidyl Peptidase 4 Inhibitor',
  },
  {
    label: 'Omnipod',
    value: 'Omnipod',
  },
  {
    label: 'Glucagon',
    value: 'Glucagon',
  },
  {
    label: 'Metformin',
    value: 'Metformin',
  },
  {
    label: 'Sulfonylureas',
    value: 'Sulfonylurea Compounds',
  },
  {
    label: 'Thiazolidinediones',
    value: 'Thiazolidinediones',
  },
];

const INSULIN_CLASSES = [
  'insulin',
  'Rapid-Short-Acting Insulin',
  'Intermediate-Long-Acting Insulin',
  'Mixed Insulin',
  'Concentrated Insulin',
  'Insulin_GLP-1 RA Combinations',
];
const isInsulinClass = cls =>
  INSULIN_CLASSES.includes(cls);

export default function DrugClassSearch() {
    const [form] = Form.useForm();
    const searchInsurancePlansByPartDCode = useAPI(api.searchInsurancePlansByPartDCode, true); 
    const searchDrugClassInfoByPartDCode = useAPI(api.searchDrugClassInfoByPartDCode);
    const sendContact = useAPI(api.sendContactInfo); 
    const [plansLoading, setPlansLoading] = useState(false);
    const getInsurancePlans = useAPI(api.getInsurancePlans);
    const getCountyFromZip = useAPI(api.getCountyFromZip);
    
    const [planSearchInput, setPlanSearchInput] = useState('');
    const logPlanSearchRequest = useAPI(api.logPlanSearch);
    const lastSentRef = useRef('');

    const [planOptions, setPlanOptions] = useState([]);
    const [results, setResults] = useState([]);
    const [, setSelectedPlan] = useState('');
    const [selectedPlanDetails, setSelectedPlanDetails] = useState({});
    const [expandedRow, setExpandedRow] = useState(null);
    const [deductible, setDeductible] = useState('');
    // const [isModalVisible, setIsModalVisible] = useState(false);
    const [isInstructionsModalVisible, setIsInstructionsModalVisible] = useState(false);
    const [isContactModalVisible, setIsContactModalVisible] = useState(false);

    // eslint-disable-next-line 
    const [isNoticeVisible, setIsNoticeVisible] = useState(true);
    // eslint-disable-next-line 
    const handleNoticeOk = () => {
      setIsNoticeVisible(false); // Close the modal when the user clicks "OK"
    };
    
    const [searchMethod, setSearchMethod] = useState(SEARCH_METHOD.BY_PARTD);
    const searchDrugClassInfo = useAPI(api.searchDrugClassInfo);
    const [selectedDrugClass, setSelectedDrugClass] = useState('');
    const [showInsulinAlert, setShowInsulinAlert] = useState(false);
    const [drugCostNote, setDrugCostNote] = useState("");

    const [drugCosts, setDrugCosts] = useState([]); // New state for drug_costs
    const [pharmacySummary, setPharmacySummary] = useState({});
    const [countyOptions, setCountyOptions] = useState([]);
    const [isCountySelectionVisible, setIsCountySelectionVisible] = useState(false);

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [detailCache, setDetailCache] = useState(initDetailCache());
    const [exportSelections, setExportSelections] = useState({});
    const [exportBusy, setExportBusy] = useState(false);
    const [exportShowM3P] = useState(false);
    // const [startDate] = useState(() => new Date());
    // const paymentPlanStartMonth = startDate.getMonth();


    useEffect(() => {
      if (!results.length) {
        setExportSelections({});
        return;
      }
      setExportSelections(prev => {
        const clone = { ...prev };
        results.forEach(r => {
          const key = drugKeyFromRecord(r);
          if (!clone[key]) {
            clone[key] = {
              drugKey: key,
                drugName: r.nonproprietaryname || r.proprietaryname || key,
              pa: false,
              breakdown: false,
              m3p: false,
              m3pStartMonth: new Date().getMonth(), // default
            };
          }
        });
        // prune removed drugs
        Object.keys(clone).forEach(k => {
          if (!results.find(r => drugKeyFromRecord(r) === k)) delete clone[k];
        });
        return clone;
      });
    }, [results]);

    function pickRecordForBreakdown(results, drugKey) {
      // collect all candidates sharing the same slug/key
      const candidates = results.filter(r => drugKeyFromRecord(r) === drugKey);
      if (!candidates.length) return null;
      if (candidates.length === 1) return candidates[0];

      // Sort by: (a) has out_of_pocket_cost length (desc), (b) has prior_authorization_details, (c) tier (asc)
      return candidates.slice().sort((a,b) => {
        const aLen = Array.isArray(a.out_of_pocket_cost) ? a.out_of_pocket_cost.length : 0;
        const bLen = Array.isArray(b.out_of_pocket_cost) ? b.out_of_pocket_cost.length : 0;
        if (bLen !== aLen) return bLen - aLen;
        const aPA = a.prior_authorization_details ? 1 : 0;
        const bPA = b.prior_authorization_details ? 1 : 0;
        if (bPA !== aPA) return bPA - aPA;
        // smaller tier_level_value preferred (if numeric)
        const aTier = Number(a.tier_level_value ?? 999);
        const bTier = Number(b.tier_level_value ?? 999);
        return aTier - bTier;
      })[0];
    }



    async function handleConfirmExport() {
      try {
        setExportBusy(true);

        const appendixItems = [];

        // entries where at least one box is checked
        const selected = Object.values(exportSelections).filter(s => s.pa || s.breakdown);

        for (const sel of selected) {
          // Always pick a representative record for this drug key
          const record = pickRecordForBreakdown(results, sel.drugKey);
          if (!record) {
            console.warn('[Export] No record found for key', sel.drugKey);
            continue;
          }

          // Ensure HTML (lazy build) via helper
          const ensured = await ensureDetailsForDrug({
            cache: detailCache,
            setCache: setDetailCache,
            record,
            needPa: sel.pa,
            needBreakdown: sel.breakdown,
            selectedDaysSupply: '30days',        // or lifted state if you add it later
            selectedPharmacyType: 'retail',      // idem
            selectedPlanDetails,
            drugCosts,
            startDate: new Date(),  
            includeM3P: sel.m3p,
            paymentPlanStartMonth: sel.m3pStartMonth,
          });

          appendixItems.push({
            drugKey: sel.drugKey,
            drugName: sel.drugName,
            paHtml: sel.pa ? ensured.paHtml : '',
            monthlyBreakdownHtml: sel.breakdown ? ensured.monthlyBreakdownHtml : ''
          });
        }

        setExportModalOpen(false);

        printDrugClassReport({
          selectedPlanDetails,
          drugCostNote,
          showInsulinAlert,
          appendixItems,
          includeAppendix: true,
          forceAppendixNewPage: true,
          customTitle: 'Drug Coverage and Cost Report',
          includeM3P: exportShowM3P
        });
      } catch (e) {
        console.error('[handleConfirmExport] failed', e);
        message.error('Failed to build export.');
      } finally {
        setExportBusy(false);
      }
    }

    useEffect(() => {
      // any drug whose classification is in INSULIN_CLASSES
      const hasInsulin = results.some(drug =>
        isInsulinClass(drug.classification)
      );
      setShowInsulinAlert(hasInsulin);
    }, [results]);

    // 1️⃣ Trigger county & plan lookup on tab switch
    useEffect(() => {
      // only when we switch into the Insurance-Name tab
      if (searchMethod === SEARCH_METHOD.BY_COUNTY_INSURANCE) {
        const zip = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode);
        if (zip && /^\d{5}$/.test(zip)) {
          // re-use your helpers:
          fetchCountiesFromZip(zip).then(handleCountySelection);
        }
      }
    // eslint-disable-next-line
    }, [searchMethod]);

    const handleDrugClassChange = value => {
        setSelectedDrugClass(value);
        form.setFieldValue(SEARCH_FORM_FIELDS.DrugClass, value);
    };

    // Fetch counties from zip code
    const fetchCountiesFromZip = async (zipCode) => {
      try {
        const response = await getCountyFromZip(zipCode);
        if (response && response.counties) {
          return response.counties; // Return the counties list
        }
        message.error('No counties found for the provided ZIP code.');
        return [];
      } catch (error) {
        console.error('Error fetching counties:', error);
        message.error('An error occurred while fetching counties.');
        return [];
      }
    };

    // Handle county selection and update state
    const handleCountySelection = (counties) => {
      if (counties.length > 1) {
        setCountyOptions(
          counties.map(county => ({
            label: county.county_name,
            value: county.county_code,
          }))
        );
        setIsCountySelectionVisible(true); // Show dropdown if multiple counties
      } else if (counties.length === 1) {
        form.setFieldValue(SEARCH_FORM_FIELDS.CountyCode, counties[0].county_code);
        setCountyOptions([]); // Clear options since it's auto-selected
        setIsCountySelectionVisible(false); // Hide dropdown
        planOptionsDepsChangeCallback(); // Trigger plan fetch
      } else {
        setCountyOptions([]);
        setIsCountySelectionVisible(false);
      }
    };

  async function planOptionsDepsChangeCallback() {
    const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode);
    console.log('Zip Code from form:', zipCode);

    if (!zipCode || zipCode.length !== 5) {
      setPlanOptions([]);
      message.error('Please enter a valid 5-digit zip code.');
      return;
    }

    try {
      // Fetch counties by zip code
      const countyResponse = await getCountyFromZip(zipCode);
      if (!countyResponse || !Array.isArray(countyResponse.counties) || countyResponse.counties.length === 0) {
        message.error('No counties found for the provided zip code.');
        return;
      }
  
      const counties = countyResponse.counties;
  
      if (counties.length === 1) {
        // If only one county is found, automatically use it
        const countyCode = counties[0].county_code;
        form.setFieldValue(SEARCH_FORM_FIELDS.CountyCode, countyCode);
        await fetchPlanOptions(countyCode); // Fetch plans based on the single county
      } else {
        // If multiple counties, show a dropdown for user selection
        setCountyOptions(
          counties.map(county => ({
            label: county.county_name,
            value: county.county_code,
          }))
        );
        setIsCountySelectionVisible(true); // Display the county selection dropdown
      }
    } catch (ex) {
      console.error('Error fetching counties by zip code:', ex);
      message.error('Error fetching counties: ' + (ex.message || ex));
    }
  }

  async function fetchPlanOptions(countyCode) {

    const includeMA = true;
    const includePDP = true;
    const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode); // Use the standardized field name
    
    if (!(includeMA || includePDP)) {
      setPlanOptions([]); // Reset plan options if no plan types are selected
      return;
    }
  
    try {
      setPlansLoading(true);
      let response;
      try {
        response = await getInsurancePlans(countyCode, includeMA, includePDP, zipCode);
      } finally {
        setPlansLoading(false);
      }
      if (response && Array.isArray(response.plans)) {
        const uniquePlans = new Map();
        response.plans.forEach(plan => {
          console.log(plan);
          const compositeKey = `${plan.contract_id}-${plan.plan_id}-${plan.segment_id}`;
          uniquePlans.set(compositeKey, plan);
        });
  
        const sortedPlanOptions = Array.from(uniquePlans.values())
          .sort((a, b) => a.plan_name.localeCompare(b.plan_name))
          .map(plan => ({
            label: `${plan.plan_name} (Formulary: ${plan.formulary_id}) - ${plan.contract_name}`,
            value: `${plan.contract_id}-${plan.plan_id}-${plan.segment_id}`, // Ensure this composite key is unique
            planDetails: {
              formulary_id: plan.formulary_id,
              contract_id: plan.contract_id,
              plan_id: plan.plan_id,
              segment_id: plan.segment_id,
              deductible: plan.deductible,
              plan_name: plan.plan_name
            }
          }));
        console.log("Sorted plan options:", sortedPlanOptions);
        setPlanOptions(sortedPlanOptions);
      }
    } catch (ex) {
      console.error('Error fetching insurance plans:', ex);
      message.error('Error fetching insurance plans: ' + (ex.message || ex));
    }
  }
  
  useEffect(() => {
    const plan = form.getFieldValue(SEARCH_FORM_FIELDS.Plan);
    if (plan && planOptions.find(option => option.value === plan) === undefined) {
      form.setFieldValue(SEARCH_FORM_FIELDS.Plan, '');
    }
  }, [planOptions, form]);

    const handleFormSubmit = async (values) => {
      const drugClass = form.getFieldValue(SEARCH_FORM_FIELDS.DrugClass);
      const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode); // Use the standardized field name

      if (searchMethod === SEARCH_METHOD.BY_PARTD) {

        const formulary_id = selectedPlanDetails.formulary_id || '';
        const contract_id = selectedPlanDetails.contract_id || ''; 
        const plan_id = selectedPlanDetails.plan_id || '';         
        const segment_id = selectedPlanDetails.segment_id || '';   
        
        console.log(selectedPlanDetails); 

        if (!formulary_id) {
          console.error('Formulary ID is missing.');
          message.error('Please select a plan before searching.');
          return;
        }

        if (!zipCode) {
          message.error('Zip Code is required.');
          return;
        }

        try {
          const data = await searchDrugClassInfoByPartDCode(drugClass, formulary_id, contract_id, plan_id, segment_id, zipCode);
          const updatedResults = data.drug_results.map(drug => ({ ...drug }));
          const drugCosts = data.drug_costs || []; // Default to an empty array if not present
          const pharmacySummary = data.pharmacy_summary || {}; // Default to an empty object if not present
          const drugCostNote = data.drug_cost_note || "";
      
          setResults(updatedResults);
          setSelectedPlan(formulary_id);
          setDrugCosts(drugCosts); // Add a new state for drug_costs
          setPharmacySummary(pharmacySummary); // Add a new state for pharmacy_summary
          setDrugCostNote(drugCostNote);
        } catch (error) {
          console.error('Error fetching drug class info by PartD Code:', error);
          message.error('An error occurred while fetching the drug class info.');
        }
      } else if (searchMethod === SEARCH_METHOD.BY_COUNTY_INSURANCE) {
        const countyCode = form.getFieldValue(SEARCH_FORM_FIELDS.CountyCode);
        console.log('selectedPlanDetails:', selectedPlanDetails); 
        const {
          formulary_id,
          contract_id,
          plan_id,
          segment_id, 
        } = selectedPlanDetails;
        
        console.log('newselectedPlanDetails:', selectedPlanDetails); 
    
        if (!countyCode) {
          console.error('County Code is missing.');
          message.error('Please enter a county name.');
          return;
        }
    
        if (!formulary_id) {
          console.error('Formulary ID is missing.');
          message.error('Please select an insurance plan.');
          return;
        }
    
        try {
          const data = await searchDrugClassInfo(countyCode, drugClass, formulary_id, contract_id, plan_id, segment_id, zipCode);
          const updatedResults = data.drug_results.map(drug => ({ ...drug }));
          const drugCosts = data.drug_costs || []; // Default to an empty array if not present
          const pharmacySummary = data.pharmacy_summary || {}; // Default to an empty object if not present
          const drugCostNote = data.drug_cost_note || "";
      
          setResults(updatedResults);
          setSelectedPlan(formulary_id);
          setDrugCosts(drugCosts);
          setPharmacySummary(pharmacySummary);
          setDrugCostNote(drugCostNote);
        } catch (error) {
          console.error('Error fetching drug class info by County and Insurance:', error);
          message.error('An error occurred while fetching the drug class info.');
        }
      }
    };
      
    const fetchPlansBasedOnPartDCode = async (partDCode) => {
      // Get the ZIP code from the form
      const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode);
      console.log("Using ZIP Code:", zipCode);

      if (!partDCode || (partDCode.length !== 8 && partDCode.length !== 11)) {
          setPlanOptions([]);
          const error = new Error('PartD/Plan Benefits Code must be 8 or 11 characters long.');
          error.help = 'PartD/Plan Benefits Code must be 8 or 11 characters long.';
          throw error;
      }

      if (!zipCode || zipCode.length !== 5) {
        setPlanOptions([]);
        message.error('Please enter a valid 5-digit zip code before entering Plan ID.');
        return;
      }

      try {
        const response = await searchInsurancePlansByPartDCode(partDCode, zipCode);
        if (response && response.formulary_id) { // Check for the existence of formulary_id or another unique field
          // Directly use the response object since it's not an array of plans
          setPlanOptions([{
            label: `${partDCode}: ${response.plan_name}`,
            value: response.formulary_id,
            planDetails: response // Storing the entire plan object for later use
          }]);
        } else {
          setPlanOptions([]);
        }
        return response;
      } catch (error) {
        console.error('Error:', error);
        const throwErr = new Error('No plan details found, please re-check your input.');
        throwErr.help = 'No plan details found, please re-check your input.';
        throw throwErr;
      }
    };
  const debouncedSearchPlans = fetchPlansBasedOnPartDCode;//debounce(fetchPlansBasedOnPartDCode, 800);
  
  useEffect(() => {
    const partDCode = form.getFieldValue(SEARCH_FORM_FIELDS.PartDCode);
    if (partDCode && (partDCode.length === 8 || partDCode.length === 11)) {
      debouncedSearchPlans(partDCode);
    }
  }, [form, debouncedSearchPlans]);

  const handlePlanChange = (value) => {
    console.log("Received value for handlePlanChange:", value);
    console.log("Type of passed value:", typeof value);
    console.log("Types in planOptions:", planOptions.map(opt => typeof opt.value));
    const selectedOption = planOptions.find(option => option.value === value);
    console.log("Selected option:", selectedOption);
    
    // Ensure selectedOption is not undefined before accessing its properties
    if (selectedOption && selectedOption.planDetails) {
      console.log('Selected plan details:', selectedOption.planDetails);
      setSelectedPlanDetails(selectedOption.planDetails);
      setDeductible(selectedOption.planDetails.deductible);
      // Update form fields based on the current search method
      if (searchMethod === SEARCH_METHOD.BY_PARTD) {
        form.setFieldsValue({ [SEARCH_FORM_FIELDS.PartDCode]: selectedOption.planDetails.formulary_id });
      } else if (searchMethod === SEARCH_METHOD.BY_COUNTY_INSURANCE) {
        form.setFieldsValue({ [SEARCH_FORM_FIELDS.Plan]: selectedOption.value });
      }
    } else {
      console.error('Selected plan details not found or no planDetails property.');
      setSelectedPlanDetails({});
      setDeductible(undefined);
      // Reset the PartDCode in the form state
      form.setFieldsValue({ [SEARCH_FORM_FIELDS.PartDCode]: '' });
      alert('Plan details are incomplete or missing.');
    }
  };

    // 1) Debounced logger (unchanged)
    const debouncedLogRef = useRef(
      debounce((value) => {
        console.log('[debouncedLog] sending logPlanSearch:', value);
        logPlanSearchRequest({ searchInput: value });
        lastSentRef.current = value;
      }, 1000)
    );
  
    // 2) onSearch handler
    const handlePlanSearch = (value) => {
      console.log('[handlePlanSearch] user typed:', value);
      setPlanSearchInput(value);
  
      // only log if length ≥ 3 and different from last sent
      if (value.length >= 3 && value !== lastSentRef.current) {
        debouncedLogRef.current(value);
      }
    };
  
    // 3) Final onBlur handler to catch their last input
    const handlePlanBlur = () => {
      const value = planSearchInput.trim();
      if (value && value !== lastSentRef.current) {
        console.log('[handlePlanBlur] final logPlanSearch:', value);
        logPlanSearchRequest({ searchInput: value });
        lastSentRef.current = value;
        // cancel any pending debounce
        debouncedLogRef.current.cancel();
      }
    };

  const handleInstructionsModalCancel = () => {
    setIsInstructionsModalVisible(false);
  };

  const handleContactModalSubmitPartD = async (values) => {
    try {
      const response = await sendContact(values); 

      if (response && response.message) {
        message.success(response.message || 'Your request has been submitted. We will contact you shortly.', 3);  // Show success message with a green checkmark
        setIsContactModalVisible(false);
      } else {
        // Handle server errors
        message.error(response.message || 'An error occurred. Please try again later.');
      }
    } catch (error) {
      // Handle network errors
      console.error('There was an error submitting the form:', error);
      message.error('There was a network error. Please try again later.');
    }
  };
  
  const handleContactModalCancel = () => {
    setIsContactModalVisible(false);
  };

  const showContactModal = () => {
    setIsInstructionsModalVisible(false);
    setIsContactModalVisible(true);
  };

  const handleSearchMethodChange = (e) => {
    const newMethod = e.target.value;
    setSearchMethod(newMethod);
  
    // Only reset the bits that differ between the two tabs:
    form.resetFields([
      SEARCH_FORM_FIELDS.PartDCode,
      SEARCH_FORM_FIELDS.SelectedPlan,      // if you named it that
      SEARCH_FORM_FIELDS.CountyCode,
      SEARCH_FORM_FIELDS.Plan,
      SEARCH_FORM_FIELDS.DrugClass,
    ]);
  };
  
  const carouselRef = useRef(null);

  function updateSelection(key, patch) {
    setExportSelections(prev => ({
      ...prev,
      [key]: { ...prev[key], ...patch }
    }));
  }

  function toggleAll(field, value) {
    setExportSelections(prev => {
      const clone = { ...prev };
      Object.keys(clone).forEach(k => { clone[k] = { ...clone[k], [field]: value }; });
      return clone;
    });
  }

 
      return (
        <>
          {/* Medicare Formulary Update Notice */}
          {/* <Modal
            title="📢 5/13 MedHug Platform Updates"
            visible={isNoticeVisible}
            onOk={handleNoticeOk}
            closable={false}
            footer={[
              <Button type="primary" key="ok" onClick={handleNoticeOk}>
                Got it!
              </Button>,
            ]}
          >
            <p style={{ lineHeight: 1.6 }}>
              We’ve rolled out two handy updates to help you get the most from MedHug:
            </p>
            <ul style={{ paddingLeft: '1.5em', lineHeight: 1.6 }}>
              <li style={{ marginBottom: '1em' }}>
                <strong>Prior Authorization Details:</strong>  
                Whenever you see <em>"Prior Authorization required"</em> in your coverage results, click 
                <strong> View Details </strong> to read the plan’s exact criteria.
              </li>
              <li style={{ marginBottom: '1em' }}>
                <strong>New in Diabetes Drug Class:</strong>  
                You can now search for <strong>Omnipod</strong> and <strong>Glucagon</strong> drug classes—just select “Diabetes Drug Class” and pick those options.
              </li>
            </ul>
            <p style={{ fontStyle: 'italic', color: '#555' }}>
              We hope these enhancements make your workflow smoother!
            </p>
          </Modal> */}
          
          <Button
            onClick={showContactModal}
            className="floating-help-button"
            style={{
              marginBottom: 16,
              backgroundColor: '#f5f5f5', // This is a light gray color similar to the table header
              color: 'black', // The text color is black
              borderColor: '#f5f5f5' // The border color is the same as the background
            }}
          >
            <strong>Email Us</strong>
          </Button>
          <Radio.Group onChange={handleSearchMethodChange} value={searchMethod} style={{ marginBottom: 16 }}>
            <Radio.Button value={SEARCH_METHOD.BY_PARTD}>Search by Plan ID</Radio.Button>
            <Radio.Button value={SEARCH_METHOD.BY_COUNTY_INSURANCE}>Search by Insurance Name</Radio.Button>
          </Radio.Group>

          <Form form={form} layout="vertical" onFinish={handleFormSubmit} style={{ maxWidth: 800 }}>
          {searchMethod === SEARCH_METHOD.BY_PARTD && (
            <>
            <Form.Item
              label="Zip Code"
              name={SEARCH_FORM_FIELDS.ZipCode} // Use the updated field name
              rules={[
                { required: true, message: 'Please enter a zip code.' },
                { pattern: /^\d{5}$/, message: 'Please enter a valid 5-digit zip code.' },
              ]}
            >
              <Input placeholder="" />
            </Form.Item>
              <Form.Item
              name={SEARCH_FORM_FIELDS.PartDCode}
              label={
                <span>
                  Medicare Plan ID{" "}
                  <button 
                    type="button" 
                    onClick={() => setIsInstructionsModalVisible(true)} 
                    className="link-button" 
                    style={{ 
                      padding: 0, 
                      backgroundColor: 'transparent', 
                      border: 'none', 
                      color: '#007bff', 
                      textDecoration: 'underline', 
                      cursor: 'pointer', // Make it clear that this is clickable
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'none'}  // Remove underline on hover
                    onMouseLeave={(e) => e.target.style.textDecoration = 'underline'}  // Restore underline on mouse out
                  >
                    (Example)
                  </button>
                </span>
              }
              rules={[{ required: true, message: 'Please enter Medicare Plan ID.' }]}
            >
            <StatusSelect
            options = {planOptions}
            onSearch={debouncedSearchPlans}
            onChange={handlePlanChange}
            showContactModal={showContactModal}
            autoFill
            />
          </Form.Item>
            <Modal
              title="How to Find Medicare Plan ID"
              visible={isInstructionsModalVisible}
              onCancel={handleInstructionsModalCancel}
              footer={[
                <Button key="back" onClick={handleInstructionsModalCancel}>
                  Close
                </Button>,
              ]}
            >
              <ul>
                <li>Check insurance card for a code starts with a letter (H, R, or S) followed by a series of 7 or 10 digits.</li>
                <li>Example codes might look like <strong>H6158-001</strong> or <strong>H6158-001-002</strong>.</li>
                <li>It usually appears on the <strong>right‑bottom</strong> of the front side of the card.</li>
                <li>Humana plans show it after “CMS”; Cigna plans display it at the top‑left.</li>
                <li>If you cannot find the code, <button type="button" onClick={showContactModal} className="link-button" style={{ padding: 0, backgroundColor: 'transparent', border: 'none', color: '#007bff', textDecoration: 'underline' }}>contact us</button> and we will help you find it.</li>
                {/* Hint */}

                <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
                  ← Click the arrows below to browse examples →
                </Text>

                {/* Carousel with arrows */}
                <Row align="middle" justify="center" gutter={16}>
                  <Col flex="auto">
                    <Carousel
                      ref={carouselRef}
                      autoplay
                      dots
                      style={{ maxWidth: 500, margin: '0 auto' }}
                    >
                      <div>
                        <Image
                          src="/AARP.png"
                          alt="Generic Medicare card example"
                          style={{ width: '100%', borderRadius: 4 }}
                          preview={false}
                        />
                      </div>
                      <div>
                        <Image
                          src="/Humana.png"
                          alt="Humana card example"
                          style={{ width: '100%', borderRadius: 4 }}
                          preview={false}
                        />
                      </div>
                      <div>
                        <Image
                          src="/Cigna.png"
                          alt="Cigna card example"
                          style={{ width: '100%', borderRadius: 4 }}
                          preview={false}
                        />
                      </div>
                    </Carousel>
                  </Col>
                  <Col>
                    <LeftOutlined
                      style={{ fontSize: 20, cursor: 'pointer' }}
                      onClick={() => carouselRef.current.prev()}
                    />
                  </Col>
                  <Col>
                    <RightOutlined
                      style={{ fontSize:20, cursor: 'pointer' }}
                      onClick={() => carouselRef.current.next()}
                    />
                  </Col>
                </Row>
              </ul>
            </Modal>
            <Modal
              title="Contact Us for Help"
              visible={isContactModalVisible}
              onCancel={handleContactModalCancel}
              footer={null}
            >
              <Form onFinish={handleContactModalSubmitPartD}>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Please input your name!' }]}
                >
                  <Input placeholder="Your Name" />
                </Form.Item>
                <Form.Item
                  name="email"
                  rules={[{ required: true, message: 'Please input your email!', type: 'email' }]}
                >
                  <Input placeholder="Your Email" />
                </Form.Item>
                <Form.Item
                  name="message"
                  rules={[{ required: true, message: 'Please provide insurance details or patient info.' }]}
                >
                  <Input.TextArea 
                    placeholder="Provide any information you have about the patient's insurance, and any issues you met using the platform."
                    rows={4}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Submit
                  </Button>
                </Form.Item>
              </Form>
            </Modal>
            </>
          )}
          {searchMethod === SEARCH_METHOD.BY_COUNTY_INSURANCE && (
            <>
              <Form.Item
                name={SEARCH_FORM_FIELDS.ZipCode}
                label="Zip Code"
                rules={[
                  { required: true, message: 'Please enter a zip code.' },
                  { pattern: /^\d{5}$/, message: 'Please enter a valid 5-digit zip code.' },
                ]}
              >
                <Input
                  onChange={async (e) => {
                    const zipCode = e.target.value.trim();
                    if (zipCode.match(/^\d{5}$/)) {
                      const counties = await fetchCountiesFromZip(zipCode);
                      handleCountySelection(counties); // Update county options
                    } else {
                      // Reset state on invalid zip code
                      setCountyOptions([]);
                      setIsCountySelectionVisible(false);
                      form.setFieldsValue({ [SEARCH_FORM_FIELDS.CountyCode]: null });
                      setPlanOptions([]);
                    }
                  }}
                />
              </Form.Item>

              {/* Dynamically render the county dropdown if multiple counties are found */}
              {isCountySelectionVisible && (
                <Form.Item
                  name={SEARCH_FORM_FIELDS.CountyCode}
                  label="Select County"
                  rules={[{ required: true, message: 'Please select a county.' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    onChange={async (value) => {
                      form.setFieldsValue({ [SEARCH_FORM_FIELDS.CountyCode]: value });
                      await fetchPlanOptions(value); // Trigger plan options update based on selected county
                    }}
                  >
                    {countyOptions.map((county) => (
                      <Select.Option key={county.value} value={county.value}>
                        {county.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              <Form.Item
                name={SEARCH_FORM_FIELDS.Plan}
                label="Insurance Name"
                rules={[{ required: true, message: 'Please select an insurance plan.' }]}
              >
                <Select
                  showSearch
                  loading={plansLoading}
                  placeholder="Search or select a plan…"
                  notFoundContent={
                    plansLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LoadingOutlined spin style={{ marginRight: 8 }} />
                        Loading, please wait…
                      </span>
                    ) : (
                      <div style={{ padding: 8, textAlign: "center", color: "#666" }}>
                        <div>
                          No plans found matching “<strong>{planSearchInput}</strong>.”<br/>
                          Please try a different insurance keyword or zip code.
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <span>
                            If you need further assistance,{" "}
                            <button
                              type="button"
                              onClick={showContactModal}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                color: "blue",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                fontSize: "inherit",
                              }}
                            >
                              leave your email here
                            </button>
                            , and we will get back to you in 24 hours.
                          </span>
                        </div>
                      </div>
                    )
                  }
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={handlePlanChange}
                  onBlur={handlePlanBlur}   
                  onSearch={(value) => {
                    setPlanSearchInput(value);
                    handlePlanSearch(value);
                    const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode);
                    if (!zipCode || !/^\d{5}$/.test(zipCode)) {
                      return;
                    }
                  }}
                  dropdownRender={(menu) => {
                    const zipCode = form.getFieldValue(SEARCH_FORM_FIELDS.ZipCode);
                    if (!zipCode || !/^\d{5}$/.test(zipCode)) {
                      return (
                        <div
                          style={{
                            padding: 12,
                            color: "#888",
                            fontStyle: "italic",
                            textAlign: "center",
                          }}
                        >
                          🔎 Please enter a valid 5-digit ZIP code first.
                        </div>
                      );
                    }
                    return menu;
                  }}
                >
                  {planOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {/* ─── COPY OF CONTACT MODAL ─── */}
              <Modal
                title="Contact Us for Help"
                visible={isContactModalVisible}
                onCancel={handleContactModalCancel}
                footer={null}
              >
                <Form onFinish={handleContactModalSubmitPartD}>
                  <Form.Item
                    name="name"
                    rules={[{ required: true, message: 'Please input your name!' }]}
                  >
                    <Input placeholder="Your Name" />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: 'Please input your email!', type: 'email' },
                    ]}
                  >
                    <Input placeholder="Your Email" />
                  </Form.Item>
                  <Form.Item
                    name="message"
                    rules={[
                      { required: true, message: 'Please provide insurance details or patient info.' },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="Provide any information you have about the patient's insurance, or provide the patient's name and date of birth and we will assist you in finding it."
                      rows={4}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Submit
                    </Button>
                  </Form.Item>
                </Form>
              </Modal>
            </>
          )}


          <Form.Item
              label="Diabetes Drug Class"
              name={SEARCH_FORM_FIELDS.DrugClass}
              rules={[{ required: true, message: 'Please select a drug class.' }]}
          >
              <Select 
                    options={drugClassSelectOptions} 
                    onChange={handleDrugClassChange}
                />
            </Form.Item>
            {isInsulinClass(selectedDrugClass) && (
              <Alert
                message="Important Note"
                description="All covered insulin products have a maximum copay of $35 per 30-day supply."
                type="info"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Search
            </Button>
          </Form>
          
          {selectedPlanDetails && (
            <div id="drug-class-plan-summary">
            <p><strong>Searched Plan Name: {selectedPlanDetails.plan_name}</strong></p>

            {searchMethod === SEARCH_METHOD.BY_COUNTY_INSURANCE &&
            selectedPlanDetails.contract_id &&
            selectedPlanDetails.plan_id !== undefined &&
            selectedPlanDetails.segment_id !== undefined && (
              <p>
                <strong>Plan ID:</strong>{" "}
                <span style={{ fontWeight: "bold", color: "black" }}>
                  {selectedPlanDetails.contract_id}-
                  {String(selectedPlanDetails.plan_id).padStart(3, "0")}-
                  {selectedPlanDetails.segment_id}
                </span>{" "}
                <span style={{ color: "black", size: 'small' }}>
                  (Please save this Plan ID for your next search.)
                </span>
              </p>
            )}
            {pharmacySummary && (pharmacySummary.Preferred?.length > 0 || pharmacySummary.In_network?.length > 0) ? (
              <div>
                <strong>
                  {pharmacySummary.Preferred?.length > 0
                    ? "Preferred Pharmacies:"
                    : "In-Network Pharmacies:"}
                </strong>{" "}
                {pharmacySummary.Preferred?.length > 0
                  ? pharmacySummary.Preferred.map(pharmacy => pharmacy.replace(/PHARMACY/i, "").trim()).join(", ")
                  : pharmacySummary.In_network?.map(pharmacy => pharmacy.replace(/PHARMACY/i, "").trim()).join(", ")}
              </div>
            ) : (
              <div>
                <strong>Pharmacy Information:</strong>
              </div>
            )}
            <p><div><strong>Plan Annual Deductible:</strong> ${typeof deductible === 'number' ? deductible.toFixed(2) : ''}</div></p>
          </div>
          )}

          {drugCostNote && (
            <div id="drug-class-retail-note">
            <Alert
              message={<span style={{ fontWeight: 'bold' }}>Retail Cost Not Available</span>}
              description={drugCostNote}
              type="warning"
              showIcon
              style={{
                margin: '10px 0', // Smaller margin above and below
                padding: '5px', // Compact padding
                fontSize: '14px', // Smaller text size
                lineHeight: '1.5', // Adjust line height for better readability
              }}
            />
            </div>
          )}

          <div id="drug-class-results-table">
          <DrugClassResultsTableNew
            results={results}
            selectedInsurancePlan={selectedPlanDetails.formulary_id}
            selectedPlanDetails={selectedPlanDetails}
            drugCosts={drugCosts}
            expandedRow={expandedRow}
            setExpandedRow={setExpandedRow}
            handlePopupOpen
          />
          {showInsulinAlert && (
                <Alert
                    message="Insulin Alert"
                    description="Insulin detected in results. Max copay $35 per 30-day supply."
                    type="warning"
                    showIcon
                />
            )}
          </div>
          {results.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="default"
                onClick={() => setExportModalOpen(true)}
                style={{ marginRight: 8 }}
              >
                Export Drug Coverage PDF
              </Button>
            </div>
          )}

          <Modal
              title="Select Appendix Items for Export"
              visible={exportModalOpen}
              onCancel={() => setExportModalOpen(false)}
              width={700}
              destroyOnClose
              okButtonProps={{ loading: exportBusy }}
              onOk={handleConfirmExport}
            >
              <div style={{ marginBottom: 12, display: 'flex', gap: 12 }}>
                <Button size="small" onClick={() => toggleAll('pa', true)}>Select All PA</Button>
                <Button size="small" onClick={() => toggleAll('pa', false)}>Clear All PA</Button>
                <Button size="small" onClick={() => toggleAll('breakdown', true)}>Select All Breakdowns</Button>
                <Button size="small" onClick={() => toggleAll('breakdown', false)}>Clear Breakdowns</Button>
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #eee', padding: 8 }}>
                {Object.values(exportSelections).map(sel => (
                  <div key={sel.drugKey} style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 4px' }}>
                    <div style={{ fontWeight: 600 }}>{sel.drugName}</div>
                    <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                      <label style={{ cursor:'pointer' }}>
                        <input
                          type="checkbox"
                          checked={sel.pa}
                          onChange={e => updateSelection(sel.drugKey, { pa: e.target.checked })}
                        /> PA Details
                      </label>
                      <label style={{ cursor:'pointer' }}>
                        <input
                          type="checkbox"
                          checked={sel.breakdown}
                          onChange={e => updateSelection(sel.drugKey, { breakdown: e.target.checked })}
                        /> Monthly Breakdown
                      </label>
                      {sel.breakdown && (
                        <>
                          <label>
                            <input
                              type="checkbox"
                              checked={sel.m3p}
                              onChange={e => updateSelection(sel.drugKey, { m3p: e.target.checked })}
                              style={{ marginRight:6 }}
                            />
                            Include M3P
                          </label>
                          {sel.m3p && (
                            <select
                              value={sel.m3pStartMonth}
                              onChange={e => updateSelection(sel.drugKey, { m3pStartMonth: +e.target.value })}
                              style={{ padding:'2px 4px', fontSize:12 }}
                            >
                              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
                                .map((m,i)=><option key={i} value={i}>{m}</option>)}
                            </select>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {!Object.keys(exportSelections).length && <div style={{ padding: 8, color:'#888' }}>No drugs loaded.</div>}
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color:'#666' }}>
                Only checked items will be appended after the main table.
              </div>
            </Modal>


        </>
      );
    }